import type { EligibilityError } from '../types'

// ── Type guards ───────────────────────────────────────────────────────────

interface FirebaseFunctionsError {
  code: string
  message: string
}

function isFirebaseFunctionsError(err: unknown): err is FirebaseFunctionsError {
  return (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    typeof (err as Record<string, unknown>).code === 'string' &&
    ((err as Record<string, unknown>).code as string).startsWith('functions/')
  )
}

const NETWORK_KEYWORDS = [
  'failed to fetch',
  'networkerror',
  'econnreset',
  'econnrefused',
  'enotfound',
  'timeout',
  'etimedout',
  'enetunreach',
  'abort',
] as const

function isNetworkError(err: unknown): boolean {
  if (err instanceof TypeError) return true
  if (!(err instanceof Error)) return false
  const msg = err.message.toLowerCase()
  return NETWORK_KEYWORDS.some(kw => msg.includes(kw))
}

// ── Message keyword detection ─────────────────────────────────────────────

const INPUT_KEYWORDS = [
  'invalid',
  'missing',
  'required',
  'not found',
] as const

function looksLikeInputError(message: string): boolean {
  const lower = message.toLowerCase()
  return INPUT_KEYWORDS.some(kw => lower.includes(kw))
}

// ── Classifier ────────────────────────────────────────────────────────────

function extractMessage(err: unknown): string {
  if (err instanceof Error) return err.message
  if (typeof err === 'string') return err
  return String(err)
}

/**
 * Classifies an eligibility check error into a user-friendly structured error.
 *
 * Pure function — no side effects, no logging, no analytics.
 */
export function classifyEligibilityError(err: unknown): EligibilityError {
  // 1. Firebase Cloud Functions errors (most common path)
  if (isFirebaseFunctionsError(err)) {
    return classifyFirebaseError(err)
  }

  // 2. Network errors (TypeError: Failed to fetch, etc.)
  if (isNetworkError(err)) {
    return {
      category: 'network',
      title: 'Connection Error',
      message: 'Could not reach the eligibility service.',
      guidance: 'Check your internet connection and try again.',
      retryable: true,
    }
  }

  // 3. Message keyword fallback
  const message = extractMessage(err)
  if (looksLikeInputError(message)) {
    return {
      category: 'input',
      title: 'Invalid Information',
      message,
      guidance: 'Check the patient details and try again.',
      retryable: false,
    }
  }

  // 4. Unknown
  return {
    category: 'unknown',
    title: 'Something Went Wrong',
    message: 'An unexpected error occurred while checking eligibility.',
    guidance: 'Try again. If the problem continues, contact VitalRev AI support.',
    retryable: true,
  }
}

// ── Firebase code routing ─────────────────────────────────────────────────

function classifyFirebaseError(err: FirebaseFunctionsError): EligibilityError {
  const code = err.code
  const message = err.message ?? ''

  switch (code) {
    case 'functions/invalid-argument': {
      const humanized = humanizeValidationMessage(message)
      return {
        category: 'input',
        title: humanized.title,
        message: humanized.message,
        guidance: humanized.guidance,
        retryable: false,
      }
    }

    case 'functions/not-found': {
      const isProvider = message.toLowerCase().includes('provider')
      return {
        category: 'input',
        title: isProvider ? 'Provider Not Found' : 'Patient Not Found',
        message,
        guidance: isProvider
          ? 'Select a different provider or add this provider in the Doctors tab.'
          : 'Verify the patient record and try again.',
        retryable: false,
      }
    }

    case 'functions/unauthenticated':
      return {
        category: 'auth',
        title: 'Session Expired',
        message: 'Your session has expired.',
        guidance: 'Please sign out and sign back in.',
        retryable: false,
      }

    case 'functions/unavailable': {
      const lower = message.toLowerCase()
      const isNetwork = NETWORK_KEYWORDS.some(kw => lower.includes(kw)) ||
        lower.includes('could not reach')
      return {
        category: isNetwork ? 'network' : 'server',
        title: isNetwork ? 'Connection Error' : 'Eligibility Service Error',
        message: isNetwork
          ? 'Could not reach the eligibility service.'
          : 'The eligibility service returned an error.',
        guidance: 'This is usually temporary. Wait a moment and try again.',
        retryable: true,
      }
    }

    case 'functions/internal':
      return {
        category: 'server',
        title: 'Processing Error',
        message: 'The eligibility response could not be processed.',
        guidance: 'Try again, or contact VitalRev AI support if it persists.',
        retryable: true,
      }

    case 'functions/failed-precondition':
      return {
        category: 'server',
        title: 'Service Not Configured',
        message: 'The eligibility service is not properly configured.',
        guidance: 'Contact VitalRev AI support.',
        retryable: false,
      }

    default:
      return {
        category: 'unknown',
        title: 'Something Went Wrong',
        message: 'An unexpected error occurred while checking eligibility.',
        guidance: 'Try again. If the problem continues, contact VitalRev AI support.',
        retryable: true,
      }
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────

interface HumanizedValidation {
  title: string
  message: string
  guidance: string
}

function humanizeValidationMessage(raw: string): HumanizedValidation {
  const lower = raw.toLowerCase()

  if (lower.includes('orgid') || lower.includes('providerid')) {
    return {
      title: 'Invalid Org. and Provider Information',
      message: 'Required org and provider fields are missing.',
      guidance: 'Check the organization and provider selection, then try again.',
    }
  }

  if (lower.includes('clinicid')) {
    return {
      title: 'Invalid Patient Information',
      message: 'Required clinic fields are missing.',
      guidance: 'Check the clinic selection and try again.',
    }
  }

  if (lower.includes('subscriber') && lower.includes('firstname')) {
    return {
      title: 'Invalid Patient Information',
      message: 'First name, last name, and member ID are all required fields.',
      guidance: 'Check the patient name, member ID, and date of birth, then try again.',
    }
  }

  return {
    title: 'Invalid Patient Information',
    message: raw,
    guidance: 'Check the patient name, member ID, and date of birth, then try again.',
  }
}
